<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useTrackStore } from '@/stores/track'
import { useCarEventsStore } from '@/stores/car-events'
import { useTranslaterStore } from '@/stores/translater'
import calcDatas from '@/helpers/calcDatas'

const emit = defineEmits(['closeModal'])

const trackStore      = useTrackStore()
const carEventsStore  = useCarEventsStore()
const translaterStore = useTranslaterStore()

const getTranslate  = computed(() => translaterStore.getTranslate)
const trackProcess  = computed(() => trackStore.trackProcess)
const trackMsg      = computed(() => trackStore.trackMsg)

const UTACarTrackFrom = ref(calcDatas('tBegin'))
const UTACarTrackTill = ref(calcDatas('tEnd'))

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

async function submitRequestTrack() {

    trackStore.setTrack([])
    trackStore.setTrackMsg({})

    const start = (new Date(UTACarTrackFrom.value).getTime()) / 1000
    const stop = (new Date(UTACarTrackTill.value).getTime()) / 1000

    await trackStore.readTrack({ 
        vld: true,
        car: props.uuid,
        start,
        stop,
        key_array: ['not_set']
    })

    await trackStore.readMileage({
        car: props.uuid,
        start,
        stop,
    })

    await carEventsStore.readEventsCar({
        uuid_car_array: props.uuid, 
        'c.limit': 1000,
        start,
        stop,
    })
}

onBeforeUnmount(() => {
    trackStore.setTrackMsg({})
})

watch(trackMsg, msg => {
    if(msg.success) {
        emit('closeModal', true)
    }
})

</script>

<template>
    <form class="uta-car-track" @submit.prevent="submitRequestTrack()">
        <div 
            v-if="'success' in trackMsg && !trackMsg.success"
            class="my-4 uta-alert uta-alert--danger"
        >
            <span class="material-icons uta-alert__icon">check_circle_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[trackMsg.text] || trackMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="trackStore.setTrackMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>
        <div class="row">
            <div class="col-12 col-lg-6 mb-4">
                <div class="uta-car-track__from">
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarTrackFrom"
                        v-model="UTACarTrackFrom"
                    >
                </div>
            </div>
            <div class="col-12 col-lg-6 mb-4">
                <div class="uta-car-till__from">
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarTrackTill"
                        v-model="UTACarTrackTill"
                    >
                </div>
            </div>
        </div>
        <div class="uta-car-track__quick">
            <button 
                type="button" 
                :disabled="trackProcess"
                class="uta-btn uta-btn--sm uta-btn--light uta-car-track__quick-btn"
                @click="
                    UTACarTrackFrom = calcDatas('tBegin'), 
                    UTACarTrackTill = calcDatas('now'), 
                    submitRequestTrack()
                "
            >
                <div class="uta-btn__text">
                    {{ getTranslate.CARMAP_TRACK_TODAY }}
                </div>
                <div class="uta-btn__spinner">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </button>
            <button 
                type="button" 
                :disabled="trackProcess"
                class="uta-btn uta-btn--sm uta-btn--light uta-car-track__quick-btn"
                @click="
                    UTACarTrackFrom = calcDatas('yBegin'), 
                    UTACarTrackTill = calcDatas('yEnd'), 
                    submitRequestTrack()
                "
            >
                <div class="uta-btn__text">
                    {{ getTranslate.CARMAP_TRACK_YESTERDAY }}
                </div>
                <div class="uta-btn__spinner">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </button>
            <button 
                type="button" 
                :disabled="trackProcess"
                class="uta-btn uta-btn--sm uta-btn--light uta-car-track__quick-btn"
                @click="
                    UTACarTrackFrom = calcDatas('week'), 
                    UTACarTrackTill = calcDatas('now'), 
                    submitRequestTrack()
                "
            >
                <div class="uta-btn__text">
                    {{ getTranslate.CARMAP_TRACK_WEEK }}
                </div>
                <div class="uta-btn__spinner">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </button>
        </div>
        <div class="uta-btns">
            <button 
                type="submit" 
                :disabled="trackProcess"
                class="uta-btn uta-btn--main"
                :class="{ 'uta-btn--process' : trackProcess }"
            >
                <div class="uta-btn__text">
                    {{ getTranslate.BTN_REQUEST }}
                </div>
                <div class="uta-btn__spinner">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </button>
        </div>
    </form>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-track.scss';
</style>